import { SoundManager } from '../managers/SoundManager';
import { UIScene } from './UIScene';

export class MenuScene extends Phaser.Scene {
  private themeMusic!: Phaser.Sound.BaseSound;
  private soundManager: SoundManager;
  
  // Store references to UI elements for repositioning
  private background!: Phaser.GameObjects.Image;
  private roundelContainer!: Phaser.GameObjects.Container;
  private buttonContainers: Phaser.GameObjects.Container[] = [];
  
  // Fixed positions for buttons as pixel values rather than percentages
  private readonly BUTTON_POSITIONS = {
    RUNNER: 480,
    CHASER: 588,
    LEADERBOARD: 696,
    TUTORIAL: 804
  };

  constructor() {
    super({ key: 'MenuScene' });
    this.soundManager = SoundManager.getInstance();
  }

  preload() {
    this.load.image('menuBackground', 'assets/images/menu_background.png');
    this.load.audio('theme', 'assets/sounds/metro_theme.ogg');
  }

  create() {
    const { width, height } = this.cameras.main;
    
    // Add background with cover behavior
    this.background = this.add.image(width / 2, height / 2, 'menuBackground');
    this.updateBackgroundScale();
    
    // Load and prepare theme music
    this.themeMusic = this.sound.add('theme', { 
      loop: true,
      volume: 0.5
    });

    // Initialize the theme in the sound manager (will play if music is enabled)
    this.soundManager.toggleThemeMusic(this.themeMusic);
    
    // If music is disabled, we still want to mark that we want this theme
    // to play when it gets enabled. toggleThemeMusic handles this automatically.
    
    this.createUndergroundStyle();
    
    // Add resize event handler
    this.scale.on('resize', this.handleResize, this);
    
    // Force a layout update after initialization to ensure correct positioning
    this.updateButtonPositions();
  }
  
  private handleResize() {
    // Check if scene is active and cameras system is available
    if (!this.scene.isActive('MenuScene') || !this.cameras || !this.cameras.main) {
      return;
    }
    
    const { width, height } = this.cameras.main;
    
    // Safely update elements only if they exist
    if (this.background) {
      this.background.setPosition(width / 2, height / 2);
      this.updateBackgroundScale();
    }
    
    // Update roundel position
    if (this.roundelContainer) {
      this.roundelContainer.setPosition(width / 2, 220);
    }
    
    // Update button positions using the new method
    this.updateButtonPositions();
  }
  
  // New method to consistently update button positions
  private updateButtonPositions() {
    const width = this.cameras.main.width;
    
    // Update station buttons positions - ensure consistent spacing regardless of screen height
    if (this.buttonContainers && this.buttonContainers.length) {
      // Position buttons at their respective fixed positions
      this.buttonContainers[0]?.setPosition(width / 2, this.BUTTON_POSITIONS.RUNNER);
      this.buttonContainers[1]?.setPosition(width / 2, this.BUTTON_POSITIONS.CHASER);
      this.buttonContainers[2]?.setPosition(width / 2, this.BUTTON_POSITIONS.LEADERBOARD);
      this.buttonContainers[3]?.setPosition(width / 2, this.BUTTON_POSITIONS.TUTORIAL);
    }
  }
  
  private updateBackgroundScale() {
    // Safety check for cameras and background
    if (!this.cameras || !this.cameras.main || !this.background) {
      return;
    }
    
    const { width, height } = this.cameras.main;
    
    // Calculate scale to cover viewport while maintaining aspect ratio
    const scaleX = width / this.background.width;
    const scaleY = height / this.background.height;
    const scale = Math.max(scaleX, scaleY);
    this.background.setScale(scale);
  }

  private createUndergroundStyle() {
    const { width } = this.cameras.main;

    // Create a container for the London Underground roundel
    this.roundelContainer = this.add.container(width / 2, 220);
    
    const circleRadius = 120;
    const barHeight = 60;
    // Bar should extend ~20% beyond circle on each side
    const barWidth = circleRadius * 3;
    
    // Create the outer circle (first/bottom layer) as a ring with lineWidth matching bar height
    const circle = this.add.circle(0, 0, circleRadius, 0x000000, 0);  // transparent fill
    circle.setStrokeStyle(barHeight, 0xDE2110);  // thick red stroke
    
    // Create white background circle to ensure clean edges
    const innerCircle = this.add.circle(0, 0, circleRadius - barHeight/2, 0xFFFFFF);
    
    // Create the center bar (third/top layer)
    const bar = this.add.rectangle(0, 0, barWidth, barHeight, 0x0009AB);
    
    // Add the title text in the bar (fourth/topmost layer)
    const titleText = this.add.text(0, 0, 'METRO TAG', {
      fontFamily: 'Gill Sans, P22 Underground, Arial',
      fontSize: '48px',
      color: '#FFFFFF',
      align: 'center'
    }).setOrigin(0.5);
    
    // Add all elements to the roundel container
    this.roundelContainer.add([circle, innerCircle, bar, titleText]);

    // Create station-style buttons
    const createStationButton = (text: string, y: number, callback: () => void) => {
      // Create station sign container
      const container = this.add.container(width / 2, y);

      // Add white background rectangle
      const bg = this.add.rectangle(0, 0, 400, 70, 0xFFFFFF);
      container.add(bg);

      // Add red bar on the left (using updated red color)
      const leftBar = this.add.rectangle(-180, 0, 40, 70, 0xDE2110);
      container.add(leftBar);

      // Add station name text
      const stationText = this.add.text(0, 0, text, {
        fontFamily: 'Gill Sans, P22 Underground, Arial',
        fontSize: '32px',
        color: '#000000',
        align: 'center'
      }).setOrigin(0.5);
      container.add(stationText);

      // Make container interactive
      container.setSize(400, 70);
      container.setInteractive()
        .on('pointerover', () => {
          bg.setFillStyle(0xEEEEEE);
          this.tweens.add({
            targets: container,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 100
          });
        })
        .on('pointerout', () => {
          bg.setFillStyle(0xFFFFFF);
          this.tweens.add({
            targets: container,
            scaleX: 1,
            scaleY: 1,
            duration: 100
          });
        })
        .on('pointerdown', callback);
      
      // Store reference to container for repositioning
      this.buttonContainers.push(container);
      
      return container;
    };

    // Create the station-style buttons with fixed positions instead of relative to height
    createStationButton('Play as Runner', this.BUTTON_POSITIONS.RUNNER, () => this.startGame('runner'));
    createStationButton('Play as Chaser', this.BUTTON_POSITIONS.CHASER, () => this.startGame('chaser'));
    createStationButton('Leaderboard', this.BUTTON_POSITIONS.LEADERBOARD, () => this.openLeaderboard());
    createStationButton('How to Play', this.BUTTON_POSITIONS.TUTORIAL, () => {
      // Use scene sleep/wake pattern instead of scene.start to preserve music
      // First check if tutorial scene exists and is initialized
      if (!this.scene.get('TutorialScene').sys.settings.active) {
        // If it doesn't exist or isn't initialized, start it behind current scene
        this.scene.launch('TutorialScene');
      } else {
        // If it exists but is asleep, just wake it
        this.scene.wake('TutorialScene');
      }
      // Sleep this scene without stopping music
      this.scene.sleep('MenuScene');
    });
  }

  private startGame(role: 'runner' | 'chaser') {
    // Stop the theme music when starting a game
    if (this.themeMusic && this.themeMusic.isPlaying) {
      this.soundManager.stopCurrentTheme();
    }
    
    // Before starting the game, sleep the menu scene
    this.scene.sleep('MenuScene');
    
    const gameScene = this.scene.get('GameScene');
    
    // Check if GameScene is paused - this is important to fix the white screen bug
    if (gameScene.scene.isPaused()) {
      // If it's paused, we need to stop it completely and start a new one
      this.scene.stop('GameScene');
      this.scene.launch('GameScene', { playerRole: role });
    }
    // Check if GameScene already exists and is in a valid state
    else if (gameScene.scene.isActive() || gameScene.scene.isSleeping()) {
      // Wake it instead of creating a new instance
      this.scene.wake('GameScene', { playerRole: role });
    } else {
      // Launch a new instance if it doesn't exist or is in an unknown state
      this.scene.launch('GameScene', { playerRole: role });
    }
  }

  /**
   * Show the leaderboard by calling into UIScene
   */
  private openLeaderboard(): void {
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (uiScene) {
      uiScene.showLeaderboard();
    }
  }

  // Add init for consistent lifecycle
  init() {
    // Reset button containers array on initialization
    this.buttonContainers = [];
  }
  
  // Handle when scene is put to sleep
  sleep() {
    console.log('MenuScene: sleep');
  }
  
  // Handle when scene is awakened
  wake() {
    console.log('MenuScene: wake');
    
    // Since UIScene may still be running in game mode, we need to ensure
    // it's properly configured for menu display
    if (this.scene.get('UIScene').scene.isActive()) {
      // Use an event to communicate with UIScene that we're in menu mode
      this.events.emit('menu-activated');
    }
    
    // Refresh positions
    if (this.cameras && this.cameras.main) {
      const { width } = this.cameras.main;
      
      if (this.roundelContainer) {
        this.roundelContainer.setPosition(width / 2, 220);
      }
      
      // Use the new method to ensure consistent button positioning
      this.updateButtonPositions();
    }
  }
  
  shutdown() {
    console.log('MenuScene: shutdown');
    
    // Ensure music stops when scene is shutdown (not when sleeping)
    if (this.themeMusic && this.themeMusic.isPlaying) {
      this.soundManager.toggleThemeMusic(this.themeMusic);
    }
    
    // Remove the resize listener to prevent memory leaks
    if (this.scale) {
      this.scale.off('resize', this.handleResize, this);
    }
  }
} 